import { getAuthHeaders } from "./auth-headers";
import type {
  AccessToken,
  CreateTokenRequest,
  CreateTokenResponse,
} from "@/types/token";

const API_BASE_URL = import.meta.env.VITE_API_URL || "";
const API_BASE = `${API_BASE_URL}/v1/dashboard/access-tokens`;

export async function listTokens(
  type?: "backend" | "frontend" | "cli",
): Promise<AccessToken[]> {
  // Don't include Content-Type for GET requests
  const headers = await getAuthHeaders(false);
  // Always include revoked tokens to show them in the UI with "revoked" badge
  const params = new URLSearchParams({ include_revoked: "true" });
  if (type) {
    params.set("type", type);
  }
  const url = `${API_BASE}?${params.toString()}`;

  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Failed to fetch tokens" }));
    throw new Error(error.message || "Failed to fetch tokens");
  }

  const data = await response.json();
  return data.tokens;
}

export async function createToken(
  data: CreateTokenRequest,
): Promise<CreateTokenResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(API_BASE, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Failed to create token" }));
    throw new Error(error.message || "Failed to create token");
  }

  return response.json();
}

export async function revokeToken(tokenId: string): Promise<AccessToken> {
  // Don't include Content-Type for POST requests without body
  const headers = await getAuthHeaders(false);

  const response = await fetch(`${API_BASE}/${tokenId}/revoke`, {
    method: "POST",
    headers,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Failed to revoke token" }));
    throw new Error(error.message || "Failed to revoke token");
  }

  return response.json();
}
